import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, FolderGit2, Briefcase } from "lucide-react";

const Header = ({ darkMode, setDarkMode }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = React.useState(false);
  const [tooltip, setTooltip] = React.useState(false);

  // mobile press timeout
  const tooltipTimeout = React.useRef(null);

  const navItems = [
    { href: "/work-experience", label: "Work", icon: <Briefcase size={14} /> },
    { href: "/view-all-projects", label: "Projects", icon: <FolderGit2 size={14} /> },
  ];

  // Show tooltip for mobile (touch)
  const handleTouchStart = () => {
    tooltipTimeout.current = setTimeout(() => setTooltip(true), 300); // short delay
  };
  const handleTouchEnd = () => {
    clearTimeout(tooltipTimeout.current);
    setTimeout(() => setTooltip(false), 1500); // hide after 1.5s
  };

  return (
    <header className="sticky top-0 z-50 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-md shadow-sm transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        
        {/* LEFT: Avatar + Nav */}
        <div className="flex items-end gap-6">
          {/* Avatar */}
          <motion.div
            onClick={() => navigate("/")}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            whileHover={{ y: 2 }}
            whileTap={{ y: 5, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className="relative cursor-pointer"
          >
            <img
              src="/avatar.png"
              alt="avatar"
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-md object-cover shadow-md border border-gray-300 dark:border-gray-700"
            />

            {/* Avatar Tooltip */}
            <AnimatePresence>
              {hovered && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 4 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 -translate-x-1/2 mt-1 bg-gray-900/90 dark:bg-black/90
                  text-white text-[10px] px-2 py-1 rounded-md border border-white/10 shadow-sm"
                >
                  Home
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Nav */}
          <nav className="hidden sm:flex items-center space-x-6 text-sm text-slate-700 dark:text-slate-300 translate-y-[2px]">
            {navItems.map((item) => (
              <motion.button
                key={item.label}
                onClick={() =>
                  item.href.startsWith("/")
                    ? navigate(item.href)
                    : window.scrollTo({
                        top: document.querySelector(item.href)?.offsetTop || 0,
                        behavior: "smooth",
                      })
                }
                className="group flex items-center gap-1 relative transition-colors hover:text-white-500 dark:hover:text-white-400"
                whileHover={{ y: -1 }}
              >
                <span className="opacity-80 group-hover:opacity-100 transition">{item.icon}</span>
                <span>{item.label}</span>
                <motion.span
                  className="absolute left-0 -bottom-[2px] h-[1.5px] w-0 bg-black dark:bg-white group-hover:w-full transition-all duration-300 rounded-full"
                />
              </motion.button>
            ))}
          </nav>
        </div>

        {/* DARK MODE TOGGLE */}
        <motion.div
          className="relative"
          onMouseEnter={() => window.innerWidth >= 640 && setTooltip(true)} // desktop only
          onMouseLeave={() => window.innerWidth >= 640 && setTooltip(false)}
        >
          <motion.button
            onClick={() => setDarkMode(!darkMode)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            aria-label="Toggle theme"
            className="ml-4 p-2 rounded-xl border border-white/20 dark:border-white/10
              bg-white/20 dark:bg-black/20 shadow-sm shadow-black/10
              hover:bg-white/30 dark:hover:bg-black/30 transition-all duration-300"
            initial={false}
            animate={{ backdropFilter: darkMode ? "blur(14px)" : "blur(6px)" }}
            transition={{ duration: 0.45, ease: "easeInOut" }}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={darkMode ? "sun" : "moon"}
                initial={{ rotate: -90, scale: 0.8, opacity: 0 }}
                animate={{ rotate: 0, scale: 1, opacity: 1 }}
                exit={{ rotate: 90, scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                {darkMode 
                  ? <Sun className="w-5 h-5 text-yellow-400" /> 
                  : <Moon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                }
              </motion.div>
            </AnimatePresence>
          </motion.button>

          {/* Tooltip */}
          <AnimatePresence>
            {tooltip && (
              <motion.div
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 10 }}
                exit={{ opacity: 0, y: 4 }}
                transition={{ duration: 0.22 }}
                className="absolute left-1/2 -translate-x-1/2 mt-1 
                  bg-gray-900 text-white text-[10px] px-2 py-1 rounded-md 
                  border border-white/10 shadow-lg whitespace-nowrap z-50"
              >
                {darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </header>
  );
};

export default Header;
